// Lehké kanály (úkol 3.6): propojení Telegramu s účtem uživatele. Párování běží přes kód z aplikace;
// dokud kód platí, stav se každé 3 s tiše obnovuje (zároveň ho obnoví SSE událost `channel.linked`).
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { PAIRING_CODE_TTL_MINUTES, type TelegramStatus } from '@wac-buddy/shared'
import { createTelegramPairing, getTelegramStatus, unlinkTelegram } from '@/api/channels'
import { ApiClientError } from '@/api/client'

export type ChannelsStatus = 'idle' | 'loading' | 'ready' | 'error'
export type ChannelActionResult = { ok: true } | { ok: false; errorKey: string }

/** Jak často se při párování ptáme na stav (3 s) a jak dlouho nejdéle (platnost kódu). */
export const PAIRING_POLL_MS = 3000
export const PAIRING_POLL_MAX_MS = PAIRING_CODE_TTL_MINUTES * 60_000

const ok: ChannelActionResult = { ok: true }
const fail = (errorKey: string): ChannelActionResult => ({ ok: false, errorKey })

export const useChannelsStore = defineStore('channels', () => {
  const telegram = ref<TelegramStatus | null>(null)
  const status = ref<ChannelsStatus>('idle')
  const busy = ref(false)

  let epoch = 0
  let pollTimer: ReturnType<typeof setInterval> | null = null
  let pollStartedAt = 0

  /** Načte stav Telegramu. `silent`: data už jsou vidět, chyba ani načítání obrazovku nerozbijí. */
  async function load(silent = false): Promise<void> {
    const seq = epoch
    if (!silent || !telegram.value) status.value = 'loading'
    try {
      const res = await getTelegramStatus()
      if (seq !== epoch) return
      telegram.value = res
      status.value = 'ready'
    } catch {
      if (seq !== epoch) return
      if (!silent || !telegram.value) status.value = 'error'
    }
  }

  function pairingActive(): boolean {
    const s = telegram.value
    if (!s || s.linked || !s.pairing) return false
    return new Date(s.pairing.expiresAt).getTime() > Date.now()
  }

  function stopPolling(): void {
    if (pollTimer !== null) clearInterval(pollTimer)
    pollTimer = null
  }

  async function tick(): Promise<void> {
    if (!pairingActive() || Date.now() - pollStartedAt >= PAIRING_POLL_MAX_MS) {
      stopPolling()
      // Kód vypršel: server už rozpracované párování nevrací, zobrazí se zase tlačítko „Propojit“.
      if (telegram.value && !telegram.value.linked && telegram.value.pairing) {
        telegram.value = { ...telegram.value, pairing: null }
      }
      return
    }
    await load(true)
    if (!pairingActive()) stopPolling()
  }

  /** Začne sledovat rozpracované párování. Bez aktivního kódu nedělá nic, opakované volání nevadí. */
  function startPolling(): void {
    if (pollTimer !== null || !pairingActive()) return
    pollStartedAt = Date.now()
    pollTimer = setInterval(() => void tick(), PAIRING_POLL_MS)
  }

  /** Vytvoří nový kód. 409 = bot na serveru není nastavený. */
  async function pair(): Promise<ChannelActionResult> {
    if (busy.value || !telegram.value) return fail('common.error')
    const seq = epoch
    busy.value = true
    try {
      const pairing = await createTelegramPairing()
      if (seq === epoch && telegram.value) {
        telegram.value = { ...telegram.value, pairing }
        startPolling()
      }
      return ok
    } catch (e) {
      if (!(e instanceof ApiClientError)) return fail('common.network')
      if (e.status === 409) return fail('settings.buddy.telegram.errors.unavailable')
      return fail('settings.buddy.telegram.errors.pair')
    } finally {
      busy.value = false
    }
  }

  async function unlink(): Promise<ChannelActionResult> {
    if (busy.value) return fail('common.error')
    const seq = epoch
    busy.value = true
    try {
      await unlinkTelegram()
      stopPolling()
      if (seq === epoch && telegram.value) {
        telegram.value = {
          ...telegram.value,
          linked: false,
          displayName: null,
          linkedAt: null,
          pairing: null,
        }
      }
      return ok
    } catch (e) {
      return fail(
        e instanceof ApiClientError ? 'settings.buddy.telegram.errors.unlink' : 'common.network',
      )
    } finally {
      busy.value = false
    }
  }

  function invalidate(): void {
    epoch++
    stopPolling()
    telegram.value = null
    status.value = 'idle'
    busy.value = false
  }

  return { telegram, status, busy, load, pair, unlink, startPolling, stopPolling, invalidate }
})
